import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import apiService from "../../ApiService";

// ---------- CONFIGS ----------
const yearOptions = Array.from({ length: 6 }, (_, i) => {
  const y = new Date().getFullYear() - i;
  return { value: String(y), label: String(y) };
});

// Month name mapping
const monthNames = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
};

const formatMonth = (monthNum) => {
  const num = parseInt(monthNum);
  return monthNames[num] || monthNum;
};

const USsummaryGroups = [
  {
    root: "Income",
    columns: [
      "Paycheck",
      "TaxRefund",
      "Cheque_Deposit",
      "Other_Income",
      "HandCash",
    ],
  },
  {
    root: "Investments/Savings",
    columns: ["Equity_Investments", "Savings", "Transfer"],
  },
  {
    root: "Expenses",
    columns: [
      "OneTimePay",
      "House_Rent",
      "House_Utilities",
      "House_Insurance",
      "Edision",
      "Laundry_Expenses",
      "Water",
      "Groceries",
      "Food_Expenses",
      "Car_Lease",
      "Car_Insurance",
      "Car_Registration_Renewal",
      "Gas_Expenses",
      "Rebtel",
      "Mobile_Bill",
      "Internet",
      "Cas_Withdrawal",
      "Shopping",
      "Entertainment",
      "Passport_Renewal",
      "Grooming",
      "Medical_Expenses",
      "Sports",
      "School",
      "HandLoan_Repayment",
      "IncomeTax",
      "Other_Expenses",
    ],
  },
];

const INDsummaryGroups = [
  {
    root: "Income",
    columns: [
      "Paycheck",
      "USA_Salary",
      "Residential_Rent",
      "House_Advance",
      "GeniusNexa_Income",
      "Renunumari_Income",
      "Gpay_Cashback",
      "Savings_Interest",
      "Dividend_Income",
      "Tax_Refund",
      "Handcash",
      "Other_Income",
    ],
  },
  {
    root: "Investments/Savings",
    columns: ["Mutual_Fund", "Pension", "Stock", "ICICI_Life_Insurance", "LIC"],
  },
  {
    root: "Expenses",
    columns: [
      "Chitfund",
      "Personal_Loan",
      "HandCash_Repayment",
      "EBBills",
      "PropertyTax",
      "Mobile_Recharge",
      "BSNL_Bills",
      "Personal_Donations",
      "Pooja_Expenses",
      "To_Prakish",
      "Diwali_Gift_To_Family",
      "To_Amma",
      "Abacus",
      "Chess_Fees",
      "ATM_Withdraw",
      "Cab_Expenses",
      "Taxfiling_Expenses",
      "GeniusNexa_Bills",
      "Credit_Card_Payment",
      "Other_Expenses",
      "Billing_Errors",
    ],
  },
];

// ---------- HELPERS ----------
const formatCurrency = (val) => {
  const num = Number(val);
  if (isNaN(num)) return "$0.00";
  return `$${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatIndCurrency = (val) => {
  const num = Number(val);
  if (isNaN(num)) return "₹0.00";
  return num.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const calculateSummary = (row, country) => {
  const groups = country === "IND" ? INDsummaryGroups : USsummaryGroups;
  const totalIncome = groups[0].columns.reduce(
    (sum, k) => sum + (Number(row[k]) || 0),
    0
  );
  const totalInvestments = groups[1].columns.reduce(
    (sum, k) => sum + (Number(row[k]) || 0),
    0
  );
  const totalExpenses = groups[2].columns.reduce(
    (sum, k) => sum + (Number(row[k]) || 0),
    0
  );
  return {
    year: row.year,
    month: row.month,
    Income: totalIncome,
    Investments: totalInvestments,
    Expenses: totalExpenses,
    Balance: totalIncome - totalInvestments - totalExpenses,
  };
};

// ---------- MAIN COMPONENT ----------
const SummaryView = () => {
  const [selectedTab, setSelectedTab] = useState("USA");
  const [selectedYears, setSelectedYears] = useState([yearOptions[0]]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const years = selectedYears.map((y) => y.value);
      const country = selectedTab === "INDIA" ? "IND" : "USA";
      const data = await apiService.fetchTransactionSummaryByYears(
        years,
        country
      );
      const records = Array.isArray(data)
        ? data
        : data.records || data.data || [];
      setSummary(records.map((row) => calculateSummary(row, country)));
    } catch (e) {
      console.error("Error fetching summary", e);
      setSummary([]);
    }
    setLoading(false);
  }, [selectedTab, selectedYears]);
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);
  // Calculate totals
  const totals = summary.reduce(
    (acc, row) => {
      acc.Income += row.Income;
      acc.Investments += row.Investments;
      acc.Expenses += row.Expenses;
      acc.Balance += row.Balance;
      return acc;
    },
    { Income: 0, Investments: 0, Expenses: 0, Balance: 0 }
  );

  // Calculate percentages
  const pct = (value) =>
    totals.Income ? ((value / totals.Income) * 100).toFixed(1) + "%" : "0%";

  return (
    <div className="max-w-7xl mx-auto bg-white">
      {/* Enhanced Filter Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border border-blue-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span className="text-xl mr-2">📊</span>
          Financial Summary Analysis
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country
            </label>
            <Select
              options={[
                { value: "USA", label: "🇺🇸 USA" },
                { value: "INDIA", label: "🇮🇳 India" },
              ]}
              value={{
                value: selectedTab,
                label: selectedTab === "USA" ? "🇺🇸 USA" : "🇮🇳 India",
              }}
              onChange={(opt) => setSelectedTab(opt.value)}
              className="react-select-container"
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base) => ({
                  ...base,
                  borderColor: "#d1d5db",
                  borderRadius: "0.5rem",
                  padding: "0.125rem",
                  "&:hover": {
                    borderColor: "#4f46e5",
                  },
                }),
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Years
            </label>
            <Select
              isMulti
              options={yearOptions}
              value={selectedYears}
              onChange={(val) => setSelectedYears(val)}
              placeholder="Select one or more years..."
              className="react-select-container"
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                control: (base) => ({
                  ...base,
                  borderColor: "#d1d5db",
                  borderRadius: "0.5rem",
                  padding: "0.125rem",
                  "&:hover": {
                    borderColor: "#4f46e5",
                  },
                }),
              }}
            />
          </div>
        </div>

        {summary.length > 0 && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-3 border border-green-200">
              <div className="text-xs text-gray-600 mb-1">Total Income</div>
              <div className="text-lg font-bold text-green-700">
                {selectedTab === "INDIA"
                  ? formatIndCurrency(totals.Income)
                  : formatCurrency(totals.Income)}
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <div className="text-xs text-gray-600 mb-1">
                Total Investments
              </div>
              <div className="text-lg font-bold text-blue-700">
                {selectedTab === "INDIA"
                  ? formatIndCurrency(totals.Investments)
                  : formatCurrency(totals.Investments)}
              </div>
              <div className="text-xs text-gray-500">
                {pct(totals.Investments)} of income
              </div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-orange-200">
              <div className="text-xs text-gray-600 mb-1">Total Expenses</div>
              <div className="text-lg font-bold text-orange-700">
                {selectedTab === "INDIA"
                  ? formatIndCurrency(totals.Expenses)
                  : formatCurrency(totals.Expenses)}
              </div>
              <div className="text-xs text-gray-500">
                {pct(totals.Expenses)} of income
              </div>
            </div>
            <div
              className={`rounded-lg p-3 border ${
                totals.Balance >= 0
                  ? "bg-green-50 border-green-300"
                  : "bg-red-50 border-red-300"
              }`}
            >
              <div className="text-xs text-gray-600 mb-1">Net Balance</div>
              <div
                className={`text-lg font-bold ${
                  totals.Balance >= 0 ? "text-green-800" : "text-red-800"
                }`}
              >
                {selectedTab === "INDIA"
                  ? formatIndCurrency(totals.Balance)
                  : formatCurrency(totals.Balance)}
              </div>
              <div className="text-xs text-gray-600">
                {pct(totals.Balance)} savings rate
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <div>
            <span className="font-semibold">Viewing:</span> {selectedTab} |{" "}
            {selectedYears.map((y) => y.label).join(", ")}
          </div>
          {summary.length > 0 && (
            <div>
              <span className="font-semibold">Records:</span> {summary.length}{" "}
              months
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600 font-medium">Loading summary...</p>
          </div>
        </div>
      ) : summary.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
          <span className="text-4xl mb-4 block">📭</span>
          <p className="text-gray-700 font-medium">No summary data found</p>
          <p className="text-gray-500 text-sm mt-2">
            Try selecting different years or country
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
          <div className="overflow-x-auto max-h-[500px]">
            <table className="min-w-full border-collapse">
              <thead className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white sticky top-0 z-20 shadow-md">
                <tr>
                  <th className="border border-blue-500 px-4 py-3 text-left font-semibold">
                    Year
                  </th>
                  <th className="border border-blue-500 px-4 py-3 text-left font-semibold">
                    Month
                  </th>
                  <th className="border border-blue-500 px-4 py-3 text-right font-semibold">
                    💰 Income
                  </th>
                  <th className="border border-blue-500 px-4 py-3 text-right font-semibold">
                    📈 Investments/Savings
                  </th>
                  <th className="border border-blue-500 px-4 py-3 text-right font-semibold">
                    💳 Expenses
                  </th>
                  <th className="border border-blue-500 px-4 py-3 text-right font-semibold">
                    💵 Balance Left
                  </th>
                </tr>
              </thead>
              <tbody>
                {summary.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`transition-colors ${
                      idx % 2 === 0
                        ? "bg-white hover:bg-blue-50"
                        : "bg-gray-50 hover:bg-blue-50"
                    }`}
                  >
                    <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700">
                      {row.year}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-gray-700">
                      {formatMonth(row.month)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right font-semibold text-green-700">
                      {selectedTab === "INDIA"
                        ? formatIndCurrency(row.Income)
                        : formatCurrency(row.Income)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right text-blue-700">
                      {selectedTab === "INDIA"
                        ? formatIndCurrency(row.Investments)
                        : formatCurrency(row.Investments)}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 text-right text-orange-700">
                      {selectedTab === "INDIA"
                        ? formatIndCurrency(row.Expenses)
                        : formatCurrency(row.Expenses)}
                    </td>
                    <td
                      className={`border border-gray-300 px-4 py-2 font-bold text-right ${
                        row.Balance >= 0
                          ? "text-green-700 bg-green-50"
                          : "text-red-700 bg-red-50"
                      }`}
                    >
                      {selectedTab === "INDIA"
                        ? formatIndCurrency(row.Balance)
                        : formatCurrency(row.Balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gradient-to-r from-gray-100 to-gray-200 sticky bottom-0 z-20 font-bold shadow-inner">
                <tr>
                  <td
                    className="border border-gray-400 px-4 py-3 text-left text-gray-800"
                    colSpan={2}
                  >
                    <span className="text-lg">📊 Total</span>
                  </td>
                  <td className="border border-gray-400 px-4 py-3 text-right text-green-800 text-lg">
                    {selectedTab === "INDIA"
                      ? formatIndCurrency(totals.Income)
                      : formatCurrency(totals.Income)}
                  </td>
                  <td className="border border-gray-400 px-4 py-3 text-right text-blue-800">
                    <div className="text-lg">
                      {selectedTab === "INDIA"
                        ? formatIndCurrency(totals.Investments)
                        : formatCurrency(totals.Investments)}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      ({pct(totals.Investments)})
                    </div>
                  </td>
                  <td className="border border-gray-400 px-4 py-3 text-right text-orange-800">
                    <div className="text-lg">
                      {selectedTab === "INDIA"
                        ? formatIndCurrency(totals.Expenses)
                        : formatCurrency(totals.Expenses)}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      ({pct(totals.Expenses)})
                    </div>
                  </td>
                  <td
                    className={`border border-gray-400 px-4 py-3 text-right ${
                      totals.Balance >= 0
                        ? "text-green-900 bg-green-100"
                        : "text-red-900 bg-red-100"
                    }`}
                  >
                    <div className="text-lg">
                      {selectedTab === "INDIA"
                        ? formatIndCurrency(totals.Balance)
                        : formatCurrency(totals.Balance)}
                    </div>
                    <div className="text-xs text-gray-700 mt-1">
                      ({pct(totals.Balance)})
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryView;
